import React, { useState, useMemo, useCallback } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, LayoutList, FileText, CheckCircle, Archive, Building2, Home, Search } from 'lucide-react'
import { useProperties, useDeleteProperty, useReorderProperties, useBuildings, Property } from '@/hooks'

import { countPropertyGroups, filterProperties, pageOf } from '@/lib/property_groups'

type FilterStatus = 'all' | 'draft' | 'published' | 'archived'
type FilterBuilding = 'all' | 'with_building' | 'standalone'

/**
 * Página de listado de propiedades con filtrado por estado, búsqueda y reordenamiento.
 * Usa Convex queries con filtrado del lado del cliente para búsqueda.
 */
export function Properties() {
  const [searchParams, setSearchParams] = useSearchParams()
  const statusParam = searchParams.get('status')
  const currentStatus: FilterStatus = ['draft', 'published', 'archived'].includes(statusParam || '') ? statusParam as FilterStatus : 'all'
  const currentBuilding = (searchParams.get('building') as FilterBuilding) || 'all'
  const requestedPage = Number(searchParams.get('page') || '1')
  const selectedBuildingId = searchParams.get('buildingId') || ''
  const searchQuery = searchParams.get('search') || ''
  const debouncedSearch = searchQuery
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)
  const [localProperties, setLocalProperties] = useState<Property[] | null>(null)

  const ITEMS_PER_PAGE = 10

  const handleSearchChange = (value: string) => {
    const next = new URLSearchParams(searchParams)
    next.set('page', '1')
    if (value) next.set('search', value)
    else next.delete('search')
    setSearchParams(next, { replace: true })
  }

  const { data, isLoading, error, refetch } = useProperties()
  const { data: buildingData, isLoading: buildingsLoading } = useBuildings()
  const buildingCounts = useMemo(() => countPropertyGroups(data ?? []), [data])

  const deleteMutation = useDeleteProperty()
  const reorderMutation = useReorderProperties()

  // Configure drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px drag before starting
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Use local properties during drag, otherwise use server data
  const displayData = localProperties ?? data

  const filteredProperties = useMemo(() => filterProperties(displayData ?? [], buildingData ?? [], {
    status: currentStatus, buildingId: selectedBuildingId, kind: currentBuilding, search: debouncedSearch,
  }), [displayData, buildingData, currentStatus, selectedBuildingId, currentBuilding, debouncedSearch])

  const totalItems = filteredProperties.length
  const { page: currentPage, totalPages, startIndex, items: properties } = pageOf(filteredProperties, requestedPage, ITEMS_PER_PAGE)
  const hasMore = currentPage < totalPages
  const buildingNames = new Map<string, string>((buildingData ?? []).map((building) => [building._id, building.name]))

  // Handle drag end - reorder properties
  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event

      if (!over || active.id === over.id || !displayData) {
        setLocalProperties(null)
        return
      }

      const oldIndex = displayData.findIndex((p: Property) => p._id === active.id)
      const newIndex = displayData.findIndex((p: Property) => p._id === over.id)

      if (oldIndex === -1 || newIndex === -1) {
        setLocalProperties(null)
        return
      }

      // Optimistically update local state
      const newOrder = arrayMove(displayData, oldIndex, newIndex) as Property[]
      setLocalProperties(newOrder)

      // Prepare order items for API - update all items with new positions
      const orderItems = newOrder.map((p: Property, index: number) => ({
        id: p._id,
        sort_order: index,
      }))

      try {
        await reorderMutation.mutateAsync(orderItems)
      } catch (err) {
        console.error('Failed to reorder properties:', err)
        // Revert to server state on error
        setLocalProperties(null)
      } finally {
        // Clear local state after update completes
        setLocalProperties(null)
      }
    },
    [displayData, reorderMutation]
  )

  function handleStatusChange(status: FilterStatus) {
    if (status === 'all') {
      searchParams.delete('status')
    } else {
      searchParams.set('status', status)
    }
    searchParams.set('page', '1') // Reset to first page
    setSearchParams(searchParams)
  }

  function handlePageChange(page: number) {
    searchParams.set('page', String(page))
    setSearchParams(searchParams)
  }

  async function handleDelete(id: string) {
    try {
      await deleteMutation.mutateAsync(id)
      setDeleteConfirmId(null)
    } catch (err) {
      console.error('Failed to delete property:', err)
    }
  }

  const statusTabs: { value: FilterStatus; label: string; icon: React.ReactNode }[] = [
    { value: 'all', label: 'Todas', icon: <LayoutList className="h-3.5 w-3.5" /> },
    { value: 'published', label: 'Publicadas', icon: <CheckCircle className="h-3.5 w-3.5" /> },
    { value: 'draft', label: 'Borradores', icon: <FileText className="h-3.5 w-3.5" /> },
    { value: 'archived', label: 'Archivadas', icon: <Archive className="h-3.5 w-3.5" /> },
  ]

  const buildingTabs: { value: FilterBuilding; label: string; icon: React.ReactNode }[] = [
    { value: 'all', label: 'Todas', icon: <LayoutList className="h-3.5 w-3.5" /> },
    { value: 'with_building', label: 'En edificio', icon: <Building2 className="h-3.5 w-3.5" /> },
    { value: 'standalone', label: 'Independientes', icon: <Home className="h-3.5 w-3.5" /> },
  ]

  function handleBuildingChange(building: FilterBuilding) {
    searchParams.delete('buildingId')
    if (building === 'all') {
      searchParams.delete('building')
    } else {
      searchParams.set('building', building)
    }
    searchParams.set('page', '1') // Reset to first page
    setSearchParams(searchParams)
  }

  // Disable drag when searching or paginating
  const isDragDisabled = !!debouncedSearch || currentPage > 1 || currentStatus !== 'all' || currentBuilding !== 'all' || !!selectedBuildingId || reorderMutation.isPending

  // Estado de carga
  if (isLoading || buildingsLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Propiedades</h1>
            <p className="mt-2 text-muted-foreground">Gestiona tus listados de propiedades</p>
          </div>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      </div>
    )
  }

  // Estado de error
  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Propiedades</h1>
            <p className="mt-2 text-muted-foreground">Gestiona tus listados de propiedades</p>
          </div>
        </div>
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6 text-center">
          <p className="text-destructive">Error al cargar propiedades</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {error instanceof Error ? error.message : 'Ocurrió un error'}
          </p>
          <button
            onClick={() => refetch()}
            className="mt-4 inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Intentar de nuevo
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Encabezado */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Propiedades</h1>
          <p className="mt-2 text-muted-foreground">
            Viviendas organizadas por promoción, con búsqueda por dirección y referencia.
          </p>
        </div>
        <Link
          to="/properties/create"
          className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          + Añadir Propiedad
        </Link>
      </div>

      {/* Filters */}
      <div className="rounded-xl border bg-card p-4 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          {/* Filter Groups */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
            {/* Status Filter */}
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Estado</span>
              <div className="flex gap-1 rounded-lg bg-muted/50 p-1">
                {statusTabs.map((tab) => (
                  <button
                    key={tab.value}
                    onClick={() => handleStatusChange(tab.value)}
                    className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-all ${
                      currentStatus === tab.value
                        ? 'bg-background text-foreground shadow-sm ring-1 ring-border/50'
                        : 'text-muted-foreground hover:bg-background/50 hover:text-foreground'
                    }`}
                  >
                    {tab.icon}
                    <span className="hidden sm:inline">{tab.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Building Filter */}
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Tipo</span>
              <div className="flex gap-1 rounded-lg bg-muted/50 p-1">
                {buildingTabs.map((tab) => (
                  <button
                    key={tab.value}
                    onClick={() => handleBuildingChange(tab.value)}
                    className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-all ${
                      currentBuilding === tab.value
                        ? 'bg-background text-foreground shadow-sm ring-1 ring-border/50'
                        : 'text-muted-foreground hover:bg-background/50 hover:text-foreground'
                    }`}
                  >
                    {tab.icon}
                    <span className="hidden sm:inline">{tab.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Search */}
          <div className="relative w-full lg:w-72">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Edificio, vivienda, referencia o dirección…"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full rounded-lg border border-input bg-background py-2 pl-9 pr-3 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
          </div>
        </div>
      </div>

      <div className="grid gap-3 rounded-xl border bg-card p-4 sm:grid-cols-[1fr_auto] sm:items-end">
        <label className="space-y-1.5 text-sm font-medium">
          <span className="block">Promoción / edificio</span>
          <select aria-label="Filtrar por promoción" value={selectedBuildingId} onChange={(event) => {
            const next = new URLSearchParams(searchParams)
            if (event.target.value) { next.set('buildingId', event.target.value); next.delete('building') }
            else next.delete('buildingId')
            next.set('page', '1'); setSearchParams(next)
          }} className="w-full rounded-md border bg-background p-2 font-normal">
            <option value="">Todas las promociones y viviendas independientes</option>
            {selectedBuildingId && !buildingNames.has(selectedBuildingId) && <option value={selectedBuildingId}>Promoción no encontrada</option>}
            {(buildingData ?? []).map((building) => <option key={building._id} value={building._id}>{building.name} · {buildingCounts.get(building._id)?.total ?? 0} viviendas</option>)}
          </select>
        </label>
        <Link to="/buildings" className="rounded-md border px-3 py-2 text-center text-sm hover:bg-muted">Ver todas las promociones</Link>
        <p className="text-sm text-muted-foreground sm:col-span-2">{totalItems} viviendas en esta selección. {buildingCounts.get('standalone')?.total ?? 0} independientes en el catálogo. Publicación web y disponibilidad comercial son estados distintos; consulta Reservas para la disponibilidad verificada.</p>
      </div>

      {/* Reorder hint */}
      {!isDragDisabled && properties.length > 1 && (
        <p className="text-sm text-muted-foreground">
          Arrastra las filas para reordenar las propiedades
        </p>
      )}

      {/* Lista de Propiedades */}
      {properties.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed bg-muted/50 p-12 text-center">
          <div className="text-4xl">🏠</div>
          <h3 className="mt-4 text-lg font-semibold">
            {debouncedSearch ? 'No hay propiedades coincidentes' : 'Aún no hay propiedades'}
          </h3>
          <p className="mt-2 text-sm text-muted-foreground">
            {debouncedSearch
              ? `Ninguna propiedad coincide con "${debouncedSearch}"`
              : 'Comienza creando tu primer listado de propiedad.'}
          </p>
          {!debouncedSearch && (
            <Link
              to="/properties/create"
              className="mt-4 inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              Crear Propiedad
            </Link>
          )}
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full">
              <thead className="border-b bg-muted/50">
                <tr>
                  {!isDragDisabled && <th className="w-10 px-2 py-3"></th>}
                  <th className="px-4 py-3 text-left text-sm font-medium">Propiedad</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Publicación web</th>
                  <th className="hidden px-4 py-3 text-left text-sm font-medium md:table-cell">Ubicación</th>
                  <th className="hidden px-4 py-3 text-left text-sm font-medium lg:table-cell">Precio</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">Acciones</th>
                </tr>
              </thead>
              <SortableContext
                items={properties.map((p: Property) => p._id)}
                strategy={verticalListSortingStrategy}
                disabled={isDragDisabled}
              >
                <tbody className="divide-y">
                  {properties.map((property: Property) => (
                    <SortablePropertyRow
                      key={property._id}
                      property={property}
                      buildingName={property.building_id ? buildingNames.get(property.building_id) || 'Edificio sin identificar' : 'Independiente'}
                      isDragDisabled={isDragDisabled}
                      deleteConfirmId={deleteConfirmId}
                      setDeleteConfirmId={setDeleteConfirmId}
                      handleDelete={handleDelete}
                      isDeleting={deleteMutation.isPending}
                    />
                  ))}
                </tbody>
              </SortableContext>
            </table>
          </div>
        </DndContext>
      )}

      {/* Paginación */}
      {totalItems > ITEMS_PER_PAGE && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Mostrando {startIndex + 1}-
            {Math.min(startIndex + ITEMS_PER_PAGE, totalItems)} de{' '}
            {totalItems} propiedades
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage <= 1}
              className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50 disabled:hover:bg-transparent"
            >
              Anterior
            </button>
            {/* Page numbers */}
            {totalPages > 1 && (
              <div className="hidden items-center gap-1 sm:flex">
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let pageNum: number
                  if (totalPages <= 5) {
                    pageNum = i + 1
                  } else if (currentPage <= 3) {
                    pageNum = i + 1
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i
                  } else {
                    pageNum = currentPage - 2 + i
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`min-w-[2rem] rounded-md px-2 py-1.5 text-sm ${
                        currentPage === pageNum
                          ? 'bg-primary text-primary-foreground'
                          : 'border hover:bg-muted'
                      }`}
                    >
                      {pageNum}
                    </button>
                  )
                })}
              </div>
            )}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={!hasMore}
              className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50 disabled:hover:bg-transparent"
            >
              Siguiente
            </button>
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación para móvil */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 sm:hidden">
          <div className="mx-4 rounded-lg bg-background p-6 shadow-lg">
            <h3 className="text-lg font-semibold">¿Eliminar Propiedad?</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Esta acción no se puede deshacer.
            </p>
            <div className="mt-4 flex gap-2">
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="flex-1 rounded-md border px-4 py-2 text-sm hover:bg-muted"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDelete(deleteConfirmId)}
                disabled={deleteMutation.isPending}
                className="flex-1 rounded-md bg-destructive px-4 py-2 text-sm text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Sortable Property Row Component
// ============================================================================

interface SortablePropertyRowProps {
  property: Property
  buildingName: string
  isDragDisabled: boolean
  deleteConfirmId: string | null
  setDeleteConfirmId: (id: string | null) => void
  handleDelete: (id: string) => void
  isDeleting: boolean
}

function SortablePropertyRow({
  property,
  buildingName,
  isDragDisabled,
  deleteConfirmId,
  setDeleteConfirmId,
  handleDelete,
  isDeleting,
}: SortablePropertyRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: property._id, disabled: isDragDisabled })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.8 : 1,
  }

  // Get location display - prefer city, fall back to region
  const location = [property.address, property.city || property.region].filter(Boolean).join(', ') || '-'

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`hover:bg-muted/50 ${isDragging ? 'bg-muted shadow-lg' : ''}`}
    >
      {/* Drag Handle */}
      {!isDragDisabled && (
        <td className="px-2 py-3">
          <button
            type="button"
            className="cursor-grab rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground active:cursor-grabbing"
            {...attributes}
            {...listeners}
            title="Arrastra para reordenar"
          >
            <GripVertical className="h-4 w-4" />
          </button>
        </td>
      )}
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          {/* Thumbnail placeholder - images handled separately */}
          <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-md bg-muted">
            <div className="flex h-full w-full items-center justify-center text-muted-foreground">
              🏠
            </div>
          </div>
          <div className="min-w-0">
            <div className="truncate font-medium">{property.title}</div>
            <div className="truncate text-sm text-muted-foreground">
              {buildingName}{property.reference_code ? ` · ${property.reference_code}` : ''}
            </div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <StatusBadge status={property.status} />
      </td>
      <td className="hidden px-4 py-3 text-sm md:table-cell">
        {location}
      </td>
      <td className="hidden px-4 py-3 text-sm lg:table-cell">
        {property.price
          ? new Intl.NumberFormat('es-ES', {
              style: 'currency',
              currency: property.price_currency || 'EUR',
              maximumFractionDigits: 0,
            }).format(property.price)
          : '-'}
      </td>
      <td className="px-4 py-3 text-right">
        <div className="flex items-center justify-end gap-2">
          <Link
            to={`/properties/${property._id}/edit`}
            className="rounded-md px-2 py-1 text-sm text-primary hover:bg-muted"
          >
            Editar
          </Link>
          {deleteConfirmId === property._id ? (
            <div className="flex items-center gap-1">
              <button
                onClick={() => handleDelete(property._id)}
                disabled={isDeleting}
                className="rounded-md bg-destructive px-2 py-1 text-sm text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
              >
                {isDeleting ? '...' : 'Sí'}
              </button>
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="rounded-md px-2 py-1 text-sm text-muted-foreground hover:bg-muted"
              >
                No
              </button>
            </div>
          ) : (
            <button
              onClick={() => setDeleteConfirmId(property._id)}
              className="rounded-md px-2 py-1 text-sm text-destructive hover:bg-destructive/10"
            >
              Eliminar
            </button>
          )}
        </div>
      </td>
    </tr>
  )
}

function StatusBadge({ status }: { status: 'draft' | 'published' | 'archived' }) {
  const styles = {
    draft: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    published: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    archived: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400',
  }

  const labels: Record<string, string> = {
    draft: 'Borrador',
    published: 'Publicado',
    archived: 'Archivado',
  }

  return (
    <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${styles[status]}`}>
      {labels[status]}
    </span>
  )
}
